/**
 * Save orchestration. Container: create-or-patch the scalars, follow the
 * master when that changed (Phase 14), sync enabled, PUT groups then slots,
 * then only the satellite sections this container owns. Master: create or
 * patch its scalars, then all three satellites. Last, the auto-send layer
 * (Phase 20) — only what changed. Sequential — each step
 * depends on the row existing.
 * For Media Devoted | Created By Ciro Autiero
 */
import { cleanHourFormula, lanesToPoolJson, type DropGenOwnSections } from '@trafficpulse/shared';

import type { ContainerDraft } from '../../../drop-generator.types';
import type { useMasterMutations } from '../../../hooks/useMasterMutations';
import type { useDropGeneratorMutations } from '../../../useDropGeneratorMutations';
import type { AutoSendPatch } from '../../board-header/autosend/use-autosend-mutations';
import { autoSendSavePatch } from '../autosend/autosend-slice';
import { draftOwnSections, draftToContainerBody } from './container-draft';
import { sectionPuts } from './master/section-bodies';
import { keepKeys } from './slots/group-keyed';

export interface PersistedFacts {
  enabled: boolean;
  masterId: string | null;
  ownSections: DropGenOwnSections;
}

interface SaveParams {
  mutations: ReturnType<typeof useDropGeneratorMutations>;
  masters: ReturnType<typeof useMasterMutations>;
  draft: ContainerDraft;
  planId: string;
  containerId: string | null;
  /** null = a create. */
  persisted: PersistedFacts | null;
  /** Phase 20 — PUT containers/:id/autosend. */
  putAutoSend: (id: string, patch: AutoSendPatch) => Promise<unknown>;
}

export async function saveContainer(params: SaveParams): Promise<string> {
  const { mutations, masters, draft, persisted } = params;
  const id = await writeScalars(params);
  if (draft.kind !== 'MASTER') await syncFollow(masters, id, draft, persisted);
  if (draft.kind !== 'MASTER' && (persisted === null || persisted.enabled !== draft.enabled)) {
    await mutations.setContainerEnabled.mutateAsync({
      id,
      enabled: draft.enabled,
    });
  }
  if (draft.kind === 'ROUTE_TEST' && draft.ownSections.gatewayPool) {
    await mutations.putConfig.mutateAsync({
      id,
      resource: 'gateway',
      body: {
        pool: lanesToPoolJson(draft.gateway.pool),
        senderDefaultsJson: draft.gateway.senderDefaults,
      },
    });
  }
  if (draft.kind !== 'MASTER') await putSchedule(mutations, id, draft);
  for (const put of sectionPuts(draft)) {
    await mutations.putConfig.mutateAsync({
      id,
      resource: put.resource,
      body: put.body,
    });
  }
  const autoSend = autoSendSavePatch(draft, persisted?.ownSections.autoSend === true);
  if (Object.keys(autoSend).length > 0) await params.putAutoSend(id, autoSend);
  return id;
}

/** Create or patch the scalars; returns the row id. Switching master config,
 *  the campaign travels with the follow (checked against the NEW group). */
async function writeScalars(params: SaveParams): Promise<string> {
  const { mutations, masters, draft, planId, containerId, persisted } = params;
  const body = draftToContainerBody(draft);
  if (containerId) {
    // kind is chosen at create. An update that sends it is rejected, so the tag and color never save.
    const { kind: _kind, ...patch } = body as typeof body & { kind?: string };
    const switching = draft.kind === 'CONTAINER' && draft.masterId !== null && persisted?.masterId !== draft.masterId;
    const { campaignWiserId: _campaign, ...withoutCampaign } = patch;
    await mutations.updateContainer.mutateAsync({
      id: containerId,
      ...(switching ? withoutCampaign : patch),
    });
    return containerId;
  }
  const created =
    draft.kind === 'MASTER'
      ? await masters.createMaster.mutateAsync({ ...body, planId })
      : await mutations.createContainer.mutateAsync({
          ...body,
          planId,
          ...(draft.masterId ? { masterId: draft.masterId } : {}),
        });
  return (created as { id: string }).id;
}

/** Follow / change own sections only when they differ from what is saved. */
async function syncFollow(
  masters: SaveParams['masters'],
  id: string,
  draft: ContainerDraft,
  persisted: PersistedFacts | null,
): Promise<void> {
  if (draft.masterId === null) return;
  const same =
    persisted !== null &&
    persisted.masterId === draft.masterId &&
    JSON.stringify(draftOwnSections(persisted.ownSections)) === JSON.stringify(draftOwnSections(draft.ownSections));
  if (same) return;
  await masters.followMaster.mutateAsync({
    id,
    masterId: draft.masterId,
    ownSections: draftOwnSections(draft.ownSections),
    ...(draft.campaignWiserId !== null ? { campaignWiserId: draft.campaignWiserId } : {}),
  });
}

/** Groups BEFORE slots: a drop may name a group linked in the same save. */
async function putSchedule(mutations: SaveParams['mutations'], id: string, draft: ContainerDraft) {
  // Slider rows still waiting for a metric never reach the API (400 there).
  const groups = draft.groups.map((g) => ({
    ...g,
    orderFormulaJson: {
      ...g.orderFormulaJson,
      formula: cleanHourFormula(g.orderFormulaJson.formula),
    },
  }));
  await mutations.putConfig.mutateAsync({
    id,
    resource: 'groups',
    body: { groups },
  });
  await mutations.putConfig.mutateAsync({
    id,
    resource: 'slots',
    body: {
      // Full replace: routeId/optionsOverrideJson must ride along even
      // without an editor UI, or every save wipes persisted slot pins.
      slots: draft.slots.map((s) => ({
        ...(s.id ? { id: s.id } : {}),
        amount: s.amount,
        amountsByGroup: s.amountsByGroup,
        listsByGroup: s.listsByGroup ?? null,
        enabled: s.enabled,
        routeId: s.routeId,
        gatewayRowId: s.gatewayRowId,
        optionsOverrideJson: s.optionsOverrideJson,
        // Phase 21 — the drop's groups and pinned minute (the container is a list)
        listGroupIds: s.listGroupIds,
        pinnedAtMin: s.pinnedAtMin,
        sendOutsideWindow: s.sendOutsideWindow ?? false, // 14/10/2026 — this drop goes outside the window too
        listsRankByGroup: keepKeys(s.listsRankByGroup, s.listGroupIds), // 19/10/2026 — ranks of groups on the drop
      })),
    },
  });
  // Phase 18b — after the groups: a list override names the container's lists.
  await mutations.putConfig.mutateAsync({
    id,
    resource: 'list-overrides',
    body: { overrides: draft.listOverrides },
  });
}
